/* BaseGrid invokes format callbacks as functions, not as React component types. */
/* eslint-disable react/no-unstable-nested-components, react/function-component-definition */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Card, CardBody, Input, Label,
} from 'reactstrap';
import moment from 'moment';
import BaseGrid from '../../components/Common/BaseGrid';
import { post } from '../../helpers/api_helper';
import { Can } from '../../utilities/Can';

export const CORE_GIFT_CARDS_URL = '/api/coregiftcards';

const formatDate = (value) => {
  if (!value) return '—';
  const date = moment(value, moment.ISO_8601, true);
  return date.isValid() ? date.format('MM/DD/YYYY') : '—';
};
const dateInput = (value) => (value ? String(value).slice(0, 10) : '');
const isSent = (value) => value === true || value === 1;
const currentDisplayName = () => {
  try {
    const user = JSON.parse(localStorage.getItem('authUser'));
    return user?.display_name?.trim() || user?.displayName?.trim() || '';
  } catch {
    return '';
  }
};
const trackingValues = (row) => ({
  quantity_sent: Number(row.quantity_sent),
  core_received_date: dateInput(row.core_received_date) || null,
  gift_card_sent: isSent(row.gift_card_sent),
  gift_card_sent_date: dateInput(row.gift_card_sent_date) || null,
  sent_by: row.sent_by?.trim() || null,
});
// Use normalized values returned by the API, falling back to the submitted patch.
const savedValues = (changes, response) => Object.fromEntries(
  Object.entries(changes).map(([key, value]) => [
    key, response && Object.prototype.hasOwnProperty.call(response, key) ? response[key] : value,
  ]),
);

export function AmazonGiftCardsWorkspace() {
  const { t } = useTranslation();
  const [order, setOrder] = useState('');
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rowUpdate, setRowUpdate] = useState(null);
  const updateRow = (id, changes) => setRowUpdate({ ...changes, id });

  const startEdit = (row) => {
    setError('');
    setDraft({
      id: row.id,
      sale_id: row.sale_id,
      reference1: row.reference1,
      quantity_sent: row.quantity_sent,
      core_received_date: dateInput(row.core_received_date),
      gift_card_sent: isSent(row.gift_card_sent),
      gift_card_sent_date: dateInput(row.gift_card_sent_date),
      sent_by: row.sent_by || '',
      staff_note: row.staff_note || '',
      original_staff_note: row.staff_note || '',
      original_tracking: trackingValues(row),
    });
  };

  const save = async () => {
    if (saving) return;
    const quantity = Number(draft.quantity_sent);
    if (String(draft.quantity_sent).trim() === '' || !Number.isInteger(quantity) || quantity < 1) {
      setError(t('QTY Sent must be a whole number greater than zero.'));
      return;
    }
    setSaving(true);
    setError('');
    let trackingSaved = false;
    let savedChanges = {};
    try {
      const values = trackingValues(draft);
      const changes = Object.fromEntries(Object.entries(values)
        .filter(([key, value]) => value !== draft.original_tracking[key]));
      if (Object.keys(changes).length > 0) {
        const response = await post(`${CORE_GIFT_CARDS_URL}/save/${draft.id}`, changes);
        const saved = savedValues(changes, response);
        savedChanges = saved;
        trackingSaved = true;
        // Keep a successful tracking save out of a notes-only retry.
        setDraft((current) => ({ ...current, original_tracking: values }));
      }
      if (draft.staff_note !== draft.original_staff_note) {
        const notes = { staff_note: draft.staff_note };
        const response = await post(`/api/sales/save/${draft.sale_id}`, notes);
        savedChanges = { ...savedChanges, ...savedValues(notes, response) };
      }
      setDraft(null);
    } catch (err) {
      const message = err.response?.data?.error || err.message || t('Unable to save gift card.');
      setError(trackingSaved
        ? `${t('Gift card tracking saved, but Staff Notes could not be saved. Your edits are still open.')} ${message}`
        : message);
    } finally {
      if (Object.keys(savedChanges).length > 0) updateRow(draft.id, savedChanges);
      setSaving(false);
    }
  };

  const toggleSent = async (row) => {
    if (saving || draft !== null) return;
    const sent = !isSent(row.gift_card_sent);
    setSaving(true);
    setError('');
    try {
      const changes = {
        gift_card_sent: sent,
        ...(sent ? {
          gift_card_sent_date: moment().format('YYYY-MM-DD'),
          sent_by: currentDisplayName() || row.sent_by || null,
        } : {}),
      };
      const response = await post(`${CORE_GIFT_CARDS_URL}/save/${row.id}`, changes);
      updateRow(row.id, savedValues(changes, response));
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('Unable to save gift card.'));
    } finally {
      setSaving(false);
    }
  };

  const editable = (field, label, type, display = (value) => value ?? '—') => (value, row) => {
    if (draft?.id !== row.id) return display(value, row);
    return (
      <Input
        aria-label={`${t(label)} — ${row.reference1}`}
        type={type}
        bsSize="sm"
        style={{ minWidth: type === 'date' ? 150 : 90 }}
        min={type === 'number' ? 1 : undefined}
        step={type === 'number' ? 1 : undefined}
        rows={type === 'textarea' ? 4 : undefined}
        disabled={saving}
        value={type === 'select' ? String(draft[field]) : draft[field]}
        onChange={(event) => {
          const next = type === 'select' ? event.target.value === 'true' : event.target.value;
          setDraft((current) => ({
            ...current,
            [field]: next,
            ...(field === 'gift_card_sent' && !current.gift_card_sent && next
              ? {
                gift_card_sent_date: moment().format('YYYY-MM-DD'),
                sent_by: currentDisplayName() || current.sent_by,
              } : {}),
          }));
        }}
      >
        {type === 'select' ? (
          <>
            <option value="false">{t('No')}</option>
            <option value="true">{t('Yes')}</option>
          </>
        ) : undefined}
      </Input>
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <h4>{t('Amazon Gift Cards')}</h4>
        <Card>
          <CardBody>
            <form
              className="mb-3"
              onSubmit={(event) => { event.preventDefault(); setFilter(order.trim()); }}
            >
              <Label for="gift-card-order">{t('Order')}</Label>
              <div className="d-flex flex-wrap gap-2">
                <Input
                  id="gift-card-order"
                  style={{ maxWidth: 360 }}
                  placeholder={t('Filter by order reference')}
                  value={order}
                  disabled={draft !== null || saving}
                  onChange={(event) => setOrder(event.target.value)}
                />
                <Button type="submit" color="primary" disabled={draft !== null || saving}>{t('Filter')}</Button>
                <Button type="button" color="light" disabled={draft !== null || saving || (!order && !filter)} onClick={() => { setOrder(''); setFilter(''); }}>
                  {t('Clear')}
                </Button>
              </div>
            </form>
            {error && <Alert color="danger">{error}</Alert>}
            {draft && (
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="text-muted">{t('Editing order {{order}}. Save or cancel before editing another row.', { order: draft.reference1 })}</span>
                <Button type="button" size="sm" color="light" disabled={saving} onClick={() => { setDraft(null); setError(''); }}>
                  {t('Discard edits')}
                </Button>
              </div>
            )}
            <BaseGrid
              key={filter}
              url={CORE_GIFT_CARDS_URL}
              filters={filter ? new URLSearchParams({ reference1: filter }).toString() : ''}
              rowUpdate={rowUpdate}
              className="table table-hover table-bordered"
              config={{
                columns: {
                  reference1: {
                    label: 'Order', sort: true, fixed: true, translate: false, link: { to: '/sales/%sale_id%' },
                  },
                  quantity_sent: { label: 'QTY Sent', sort: true, format: editable('quantity_sent', 'QTY Sent', 'number') },
                  core_received_date: { label: 'Date Core Rec’d', sort: true, format: editable('core_received_date', 'Date Core Rec’d', 'date', formatDate) },
                  gift_card_sent: {
                    label: 'Gift Card Sent Y/N',
                    sort: true,
                    format: editable('gift_card_sent', 'Gift Card Sent Y/N', 'select', (value, row) => {
                      const sent = isSent(value);
                      return (
                        <Button
                          type="button"
                          size="sm"
                          color={sent ? 'success' : 'secondary'}
                          aria-label={`${t('Gift Card Sent')} — ${row.reference1}`}
                          aria-pressed={sent}
                          title={t(sent ? 'Mark as not sent' : 'Mark as sent')}
                          disabled={saving || draft !== null}
                          onClick={() => toggleSent(row)}
                        >
                          {t(sent ? 'Yes' : 'No')}
                        </Button>
                      );
                    }),
                  },
                  gift_card_sent_date: { label: 'Date GC Sent', sort: true, format: editable('gift_card_sent_date', 'Date GC Sent', 'date', formatDate) },
                  sent_by: {
                    label: 'Who Sent', sort: true, translate: false, format: editable('sent_by', 'Who Sent', 'text'),
                  },
                  staff_note: {
                    label: 'Staff Notes',
                    translate: false,
                    style: { minWidth: 260, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
                    format: editable('staff_note', 'Staff Notes', 'textarea'),
                  },
                  actions: {
                    label: '',
                    format: (_, row) => (draft?.id === row.id ? (
                      <div className="d-flex gap-2">
                        <Button type="button" size="sm" color="primary" disabled={saving} onClick={save}>{t(saving ? 'Saving...' : 'Save')}</Button>
                        <Button type="button" size="sm" color="light" disabled={saving} onClick={() => { setDraft(null); setError(''); }}>{t('Cancel')}</Button>
                      </div>
                    ) : (
                      <Button type="button" size="sm" color="light" disabled={draft !== null || saving} onClick={() => startEdit(row)} aria-label={`${t('Edit')} ${row.reference1}`}>{t('Edit')}</Button>
                    )),
                  },
                },
                pagination: { sort: 'core_received_date', direction: 'desc', perPage: 30 },
              }}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default function AmazonGiftCards() {
  return (
    <>
      <Can i="see" this="Customer Service"><AmazonGiftCardsWorkspace /></Can>
      <Can not i="see" this="Customer Service">
        <div className="page-content"><Alert color="danger">Unauthorized</Alert></div>
      </Can>
    </>
  );
}
